// WHAT THE GROUND IS. Matt, 2026-09-19: "Different terrains should have
// different effects. They should maybe require extra movement to get into.
// They should provide different resources... It'd be fun to find like
// ancient ruins and things when I'm exploring the map."
//
// One table. Every terrain says what it is called, what it costs to walk
// into, and what a day's work there brings back - so the hills are a
// quarry, the shore is a fishery and the forest is a hunt, and the choice
// of where to stand is a real one. Pure; map.js has the geometry, this has
// the meaning.

import {
  baseTerrain, siteAt, isTown, hexDistance, hash, key
} from './map';

// `step` is workers to walk in. Rough ground costs two, and Roads buys that
// back (modsOf().marchRange, which used to mean "two hexes a march").
export const TERRAIN = {
  grass: {
    name: 'the meadows', step: 1, text: 'Open grass. Easy walking, some game.',
    work: { label: 'Hunt', resource: 'food', faces: 3, base: 0 },
  },
  'grass-forest': {
    name: 'the forest', step: 2, text: 'Thick woods. Slow walking, more game.',
    work: { label: 'Hunt', resource: 'food', faces: 3, base: 1 },
  },
  'grass-hill': {
    name: 'the hills', step: 2, text: 'Rising ground, with stone to quarry.',
    work: { label: 'Quarry', resource: 'goods', faces: 3, base: 0 },
  },
  'stone-hill': {
    name: 'the hills', step: 2, text: 'Rising ground, with stone to quarry.',
    work: { label: 'Quarry', resource: 'goods', faces: 3, base: 0 },
  },
  'stone-mountain': {
    name: 'the mountains', step: 2, text: 'Hard walking, and the richest ore on the island.',
    work: { label: 'Mine', resource: 'goods', faces: 3, base: 1 },
  },
  stone: {
    name: 'the rocks', step: 2, text: 'Bare stone to quarry. Nothing to eat.',
    work: { label: 'Quarry', resource: 'goods', faces: 3, base: 0 },
  },
  'stone-rocks': {
    name: 'the rocks', step: 2, text: 'Bare stone to quarry. Nothing to eat.',
    work: { label: 'Quarry', resource: 'goods', faces: 3, base: 0 },
  },
  sand: {
    name: 'the shore', step: 1, text: 'Sand and shallows. Fish.',
    work: { label: 'Fish', resource: 'food', faces: 3, base: 0 },
  },
  'sand-rocks': {
    name: 'the shore', step: 1, text: 'Sand and shallows. Fish.',
    work: { label: 'Fish', resource: 'food', faces: 3, base: 0 },
  },
  dirt: { name: 'the bare ground', step: 1, text: 'Worked out.', work: null },
  water: { name: 'the sea', step: 0, text: 'Open water.', work: null },
  'water-rocks': { name: 'the sea', step: 0, text: 'Open water.', work: null },
  'water-island': { name: 'the sea', step: 0, text: 'Open water.', work: null },
};

// Home ground, in three rings. Matt, 2026-09-19: "Way more hexes are called
// the town than I would have expected" - nineteen of them were, because
// that is how far the muster rule reaches. The RULE still reaches that far;
// only the names got finer.
// Nothing to hunt at home: the town is fields and houses, and feeding it is
// the Stewards' business. Matt, 2026-09-19: "I shouldn't be able to hunt in
// town."
export const TOWN_SQUARE = {
  name: 'the square', step: 1, work: null,
  text: 'The middle of the city. The great work stands here.',
};
export const TOWN_GROUND = {
  name: 'the town', step: 1, work: null,
  text: 'Houses. Units train and reinforce here.',
};
// Playtest 12: this was "the town's edge... still the town", and counted as
// road, while the wall drew the town a ring smaller. Now it is what it looks like.
export const TOWN_EDGE = {
  name: 'outside the walls', step: 1, work: null,
  text: 'Just outside the city wall. A road can start here.',
};
export const FOUND_GROUND = {
  name: 'new land', step: 1, text: 'Land the commonwealth has found.',
  work: { label: 'Hunt', resource: 'food', faces: 3, base: 0 },
};
export const UNKNOWN_GROUND = {
  name: 'unknown ground', step: 1, text: 'Nobody has stood here. Walk on and find out.',
  work: null,
};

/** What sort of ground a hex is, as an entry from the tables above. */
export const groundAt = (civ, q, r) => {
  if (isTown(q, r)) {
    const d = hexDistance(q, r);
    if (d === 0) return TOWN_SQUARE;
    // Named for what the wall shows: inside it is the town (playtest 12).
    const walled = civ?.city?.hexes ? civ.city.hexes.some(([a, b]) => a === q && b === r) : d === 1;
    return walled ? TOWN_GROUND : TOWN_EDGE;
  }
  const site = siteAt(q, r, civ);
  if (site >= 0) {
    const regionId = (civ?.regionSites || {})[site];
    const found = Boolean(regionId) && (civ?.regions || []).includes(regionId);
    return found ? FOUND_GROUND : UNKNOWN_GROUND;
  }
  return TERRAIN[baseTerrain(q, r, civ?.mapSeed || 0)] || TERRAIN.grass;
};

/**
 * Workers to walk into this hex. Rough ground costs two; Roads (and
 * anything else that raises `marchRange`) buys the difference back, which
 * is what a road IS.
 */
export const stepCost = (civ, q, r, ease = 0) => {
  const ground = groundAt(civ, q, r);
  return Math.max(1, (ground.step || 1) - Math.max(0, ease));
};

/** A day's work here: what it is called, what it pays, and the die. */
export const workAt = (civ, q, r) => groundAt(civ, q, r).work;

// ------------------------------------------------------------- what is out there
// Things to walk into and pick up. Matt: "It'd be fun to find like ancient
// ruins and things when I'm exploring the map." Seeded once at the start
// from the game's own hash, so two tables looking at one game agree.
export const FINDS = {
  // "A stone head", not "ancient ruins": the map already has a region
  // called The Ruins, and the two were taken for one (playtest 12, Brian:
  // "it is also called the ruins"). The key stays `ruins` for saves.
  ruins: {
    name: 'A stone head', model: 'nature/statue_head', gain: { knowledge: 2 },
    text: 'Older than the town. The Scholars will want to see it.',
  },
  grove: {
    name: 'An old grove', model: 'nature/tree_oak', gain: { food: 2, happiness: 1 },
    text: 'Fruit trees nobody planted.',
  },
  wreck: {
    name: 'A wreck', model: 'nature/canoe', gain: { goods: 2 },
    text: 'A boat broken on the rocks, worth stripping.',
  },
  lode: {
    name: 'A lode', model: 'td/detail-crystal-large', gain: { coin: 2 },
    text: 'Bright metal in the rock face.',
  },
};
export const FIND_ORDER = ['ruins', 'grove', 'wreck', 'lode'];

/**
 * Scatter the things worth finding. Wild ground only, never the town, never
 * a site (those have their own reveal), one to a hex, deterministic from
 * the seed so a reload finds the same island.
 */
export const scatterFinds = (civ, seed = 1, howMany = 5) => {
  const spots = [];
  const R = 8; // old saves only: the frontier places finds now (engine/frontier.js)
  for (let q = -R; q <= R; q += 1) {
    for (let r = -R; r <= R; r += 1) {
      if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > R) continue;
      if (isTown(q, r) || siteAt(q, r) >= 0) continue;
      const ground = groundAt(civ, q, r);
      if (!ground.work) continue; // the sea, and worked-out ground
      spots.push([q, r, hash(q, r, seed + 77)]);
    }
  }
  spots.sort((a, b) => a[2] - b[2]);
  return spots.slice(0, howMany).map(([q, r], i) => ({
    id: `f${i + 1}`,
    kind: FIND_ORDER[Math.floor(hash(q, r, seed + 91) * FIND_ORDER.length) % FIND_ORDER.length],
    q,
    r,
  }));
};

export const findAt = (civ, q, r) => (civ?.finds || []).find((f) => f.q === q && f.r === r) || null;
export const findKey = (f) => key(f.q, f.r);
